import { useEffect, useState } from 'react';

import { useBitcoinPrice } from '@/hooks/api/useBitcoinPrice';
import { useHistoryPrice } from '@/hooks/api/useHistoryPrice';
import { Icons } from '@/components/icons';
import { commonIntervals } from '@/components/interval-button/utils';
import { formatUsdValue } from '@/utils/formatUsdValue';
import { cn } from '@/utils/cn';
import { LoadingBox } from '@/components/spinner';
import { Interval } from '@/hooks/api/api.types';

interface BitcoinBannerProps {
  isOnlyChanging?: boolean;
}

const BitcoinBanner: React.FC<BitcoinBannerProps> = ({ isOnlyChanging }: BitcoinBannerProps) => {
  const [interval, setInterval] = useState<Interval>('24h');
  const [change, setChange] = useState<number>(0);

  const { data: price, isLoading: isLoadingPrice } = useBitcoinPrice();
  const { data: bitcoinData, isLoading } = useHistoryPrice({ interval });

  const bitcoinDataChange = Number(bitcoinData?.change || 0);

  useEffect(() => {
    if (bitcoinDataChange) {
      setChange(bitcoinDataChange);
    }
  }, [bitcoinDataChange]);

  const isDown = change < 0;

  const onIntervalClick = () => {
    setInterval((v) => {
      const index = commonIntervals.indexOf(v);
      const next = index === 3 ? 0 : index + 1;

      return commonIntervals[next];
    });
  };

  if (isOnlyChanging && !!change) {
    // TODO: reuse this code
    return (
      <button
        onClick={onIntervalClick}
        className={cn('flex flex-row select-none gap-1 items-center group', {
          'text-text-secondary': isLoading,
          'text-red-100': isDown && !isLoading,
          'text-green-100': !isDown && !isLoading,
        })}
      >
        <div className={cn({ 'rotate-180': !isDown, 'animate-pulse': isLoading })}>
          <Icons.triangleDown />
        </div>
        <p className={cn('text-sm', { 'animate-pulse': isLoading })}>
          {change.toString().replace('-', '')}
          <span className='text-sm'>%</span>
        </p>
        <p className='uppercase group-hover:opacity-75 text-sm'>({interval})</p>
      </button>
    );
  }

  return (
    <div
      id='BitcoinBanner'
      className='hidden lg:flex bg-surface-secondary py-2 px-3 h-11 rounded-full text-text-primary flex-row items-center gap-3'
    >
      <Icons.bitcoin />
      <div className='hidden xl:flex flex-row gap-1 whitespace-nowrap'>
        Bitcoin
        <div className='opacity-50'>BTC</div>
      </div>
      <div className='w-[2px] h-6 bg-gray-400 mx-1' />
      {isLoadingPrice ? (
        <LoadingBox width={60} height={24} className='before:via-text-primary/30' />
      ) : (
        <div className='whitespace-nowrap'>{formatUsdValue(price?.USD)}</div>
      )}
      {/* TODO: change div (non-clickable element) to button */}
      {isLoading ? (
        <LoadingBox width={100} height={24} className='hidden xl:block before:via-text-primary/30' />
      ) : (
        !!change && (
          <div
            onClick={onIntervalClick}
            className={cn('hidden xl:flex flex-row select-none gap-1 items-center cursor-pointer group', {
              'text-text-secondary': isLoading,
              'text-red-100': isDown && !isLoading,
              'text-green-100': !isDown && !isLoading,
            })}
          >
            <div className={cn({ 'rotate-180': !isDown, 'animate-pulse': isLoading })}>
              <Icons.triangleDown />
            </div>
            <div className={cn({ 'animate-pulse': isLoading })}>
              {change.toString().replace('-', '')}
              <span className='text-xs'>%</span>
            </div>
            <div className='uppercase group-hover:opacity-75'>({interval})</div>
          </div>
        )
      )}
    </div>
  );
};

export default BitcoinBanner;
